import clsx from 'clsx'
import { upperFirst } from 'lodash-es'

import { FvLinkButton, PhoneNumberDisplay } from '@fv/client-components'
import {
  addressFuncs,
  displayLocShipType,
  getLocAccessorials,
  militaryToStandardTime,
} from '@fv/client-core'
import type { HoldAtLocation, RefNum, ShipmentLocation } from '@fv/client-types'
import type { Workflow } from '@fv/models'

import type { TerminalAndType } from '@/features/quote/useTerminals'
import { useTypeNames } from '@/hooks/settings'
import { getApplicableAccessorials } from '@/utils/locationFuncs'

import { Pinline } from '../Pinline'

type StopCardProps = {
  expanded: boolean
  isOrigin?: boolean
  location: ShipmentLocation
  toggleExpanded: () => void
  workflow: Workflow
  holdAtLocation?: HoldAtLocation
  terminal?: TerminalAndType
}

const stopCardTypeStyles = {
  origin: {
    container: 'bg-fv-orange-25 border-fv-orange-300',
    header: 'bg-fv-orange',
    pinline: '!border-fv-orange-300',
  },
  destination: {
    container: 'bg-fv-green-50 border-fv-green',
    header: 'bg-fv-green',
    pinline: '!border-fv-green',
  },
  stop: {
    container: 'bg-fv-beer-xlight border-fv-beer-medium',
    header: 'bg-[#d4d4a3]',
    pinline: '!border-fv-beer-medium',
  },
} as const

type StopCardType = keyof typeof stopCardTypeStyles

export const StopCard = ({
  expanded,
  isOrigin,
  location,
  toggleExpanded,
  workflow,
  holdAtLocation,
  terminal,
}: StopCardProps) => {
  const {
    address,
    address2,
    adminArea3,
    city,
    closesAt,
    company,
    contactEmail,
    contactName,
    contactPhone,
    contactPhoneExt,
    country,
    instructions,
    opensAt,
    postalCode,
    refNums,
    sequence,
    state,
    stopType,
    type,
    shipType,
  } = location

  const accessorials = getApplicableAccessorials(location, workflow)
  const shipTypeLabel = displayLocShipType({ shipType, accessorials })
  const typeNames = useTypeNames()
  const hasAccessorials = !!accessorials?.length
  const hasContactInfo = !!(contactEmail || contactName || contactPhone)
  const hasDockHours = !!(opensAt && closesAt)
  const hasRefNum = !!refNums?.length
  const stopName = type === 'stop' ? `stop ${sequence}` : type
  const stopTypeLabel =
    stopType === 'both' ? (isOrigin ? 'pickup' : 'delivery') : stopType
  const stopCardStyles =
    stopCardTypeStyles[type as StopCardType] ?? stopCardTypeStyles.stop

  const accessorialList = getLocAccessorials({ accessorials }, typeNames)
    ?.map(a => {
      let label = a.name
      if (
        !['sort-and-segregate', 'driver-assist', 'notify', 'schedule'].includes(
          a.key,
        ) &&
        stopType !== 'both'
      ) {
        label += ` ${stopType}`
      }

      if (a.appointmentTime) {
        label += ` (${a.appointmentTime})`
      }

      return label
    })
    .join(' / ')

  const sanitizedInstructions =
    instructions?.replace(/\u00A0/g, ' ') ?? instructions

  return (
    <div
      className={clsx(
        `relative grow-0 basis-[49%] p-4 pt-8 border`,
        stopCardStyles.container,
      )}
    >
      <FvLinkButton
        className="absolute top-2 right-2"
        icon="arrows-from-line"
        onClick={toggleExpanded}
      />
      <div
        className={clsx(
          'absolute -left-[.35rem] -top-[.35rem] py-1 px-4 text-white rounded-xs',
          stopCardStyles.header,
        )}
      >
        {isOrigin ? 'From' : 'To'} {stopName}
      </div>
      <h3 className="mb-0">{company}</h3>
      <Pinline className={clsx('my-2', stopCardStyles.pinline)} />
      <p className="mb-0">
        {address}
        <br />
        {address2}
        {address2 && <br />}
        {adminArea3}
        {adminArea3 && <br />}
        {city}, {state} {postalCode} - {country.toLocaleUpperCase()}
        {hasRefNum && <br />}
        <span style={{ overflowWrap: 'anywhere' }}>
          {displayRefNums({ isOrigin, refNums, workflow })}
        </span>
        {!isOrigin && workflow === 'parcel' && holdAtLocation && (
          <span>
            <Pinline className={clsx('my-2', stopCardStyles.pinline)} dashed />
            Hold at {holdAtLocation.inLocation}
            {holdAtLocation.inLocation && <br />}
            {holdAtLocation.locTypeDisplay}
            <br />
            {holdAtLocation.address}
            {holdAtLocation.address2 && <br />}
            {address2}
            <br />
            {holdAtLocation.city}, {holdAtLocation.state}{' '}
            {holdAtLocation.postalCode} - {holdAtLocation.country}
          </span>
        )}
        {expanded && (
          <span>
            <Pinline className={clsx('my-2', stopCardStyles.pinline)} dashed />
            {hasDockHours && `${upperFirst(stopTypeLabel)} hours `}
            {hasDockHours && militaryToStandardTime(opensAt)}
            {hasDockHours && ' - '}
            {hasDockHours && militaryToStandardTime(closesAt)}
            {!hasDockHours && `No ${stopTypeLabel} hours given`}
            {(hasAccessorials || shipTypeLabel) && (
              <Pinline
                className={clsx('my-2', stopCardStyles.pinline)}
                dashed
              />
            )}
            {shipTypeLabel && shipTypeLabel}
            {shipTypeLabel && <br />}
            {accessorialList}
            {hasContactInfo && (
              <Pinline
                className={clsx('my-2', stopCardStyles.pinline)}
                dashed
              />
            )}
            {contactName}
            {contactName && <br />}
            {contactPhone && (
              <PhoneNumberDisplay val={contactPhone} ext={contactPhoneExt} />
            )}
            {contactPhone && <br />}
            {contactEmail}
            {sanitizedInstructions && (
              <Pinline
                className={clsx('my-2', stopCardStyles.pinline)}
                dashed
              />
            )}
            {sanitizedInstructions}
            {terminal && (
              <>
                <Pinline
                  className={clsx('my-2', stopCardStyles.pinline)}
                  dashed
                />
                <p>
                  {terminal.terminalCode
                    ? `Terminal code: ${terminal.terminalCode}`
                    : ''}
                  <br /> {terminal.address} <br />{' '}
                  {addressFuncs.cityStateZip(terminal)}
                  <br />
                  <PhoneNumberDisplay val={terminal.contactPhone} />
                </p>
              </>
            )}
          </span>
        )}
      </p>
    </div>
  )
}

function displayRefNums({
  isOrigin,
  refNums,
  workflow,
}: {
  isOrigin?: boolean
  refNums?: RefNum[]
  workflow: Workflow
}) {
  if (!refNums?.length) return null

  const label =
    workflow === 'truckload'
      ? 'Ref #'
      : isOrigin
        ? "Shipper's #"
        : 'Purchase order #'

  return `${label} ${refNums.map(n => n.value).join(' / ')}`
}
